import { useCallback, useRef, useState } from "react";
import { accessToken, supabase } from "@/lib/supabase";
import type { MessageRow } from "@/types/db";

/** Fase visible mientras no hay texto: la gente aguanta la espera si sabe qué está pasando. */
export type Fase = "leyendo" | "pensando" | "herramientas" | "redactando" | "reintentando";
export interface ChatMsg { id: string; dbId?: string; role: "user" | "assistant"; text: string; tools: string[]; status: "ok" | "streaming" | "error" | "refusal"; fase?: Fase; desde?: number; intento?: number; code?: string }

type Ev = { type: "meta"; conversationId: string } | { type: "status"; phase: string } | { type: "text"; text: string } | { type: "tool"; name: string; status: string } | { type: "done" } | { type: "refusal" } | { type: "error"; code?: string; message: string };

// Reintentos automáticos solo ante fallas transitorias (asesor saturado, 5xx, conexión cortada) y solo si todavía no llegó texto.
const REINTENTOS = 2;
const ESPERA_REINTENTO_MS = [3000, 7000];

/** Chat con streaming SSE contra /api/chat. Historial de la conversación vía Supabase (RLS). */
export function useChatStream() {
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [streaming, setStreaming] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const ultimoTextoRef = useRef<string>("");

  const load = useCallback(async (id: string | null) => {
    setConversationId(id);
    if (!id) { setMessages([]); return; }
    const { data } = await supabase.from("messages").select("id, role, text, content, created_at").eq("conversation_id", id).order("created_at");
    const rows = (data ?? []) as Pick<MessageRow, "id" | "role" | "text" | "content" | "created_at">[];
    const out: ChatMsg[] = [];
    for (const r of rows) {
      if (r.role === "user") {
        // los tool_result van como user sin texto: no se muestran
        if (r.text) out.push({ id: r.id, dbId: r.id, role: "user", text: r.text, tools: [], status: "ok" });
      } else {
        const tools = Array.isArray(r.content) ? (r.content as { type: string; name?: string }[]).filter((b) => b.type === "tool_use").map((b) => b.name ?? "") : [];
        const last = out[out.length - 1];
        if (last && last.role === "assistant" && !last.text && !r.text) { last.tools.push(...tools); continue; }
        if (last && last.role === "assistant" && last.status === "ok" && !last.text) { last.text = r.text ?? ""; last.tools.push(...tools); last.dbId = r.id; continue; }
        out.push({ id: r.id, dbId: r.id, role: "assistant", text: r.text ?? "", tools, status: "ok" });
      }
    }
    setMessages(out.filter((m) => m.role === "user" || m.text || m.tools.length));
  }, []);

  const send = useCallback(async (text: string): Promise<string | null> => {
    const token = await accessToken();
    if (!token) return "Tu sesión terminó. Vuelve a entrar.";
    ultimoTextoRef.current = text;
    const userId = `u-${Date.now()}`, asstId = `a-${Date.now()}`;
    setMessages((xs) => [...xs, { id: userId, role: "user", text, tools: [], status: "ok" }, { id: asstId, role: "assistant", text: "", tools: [], status: "streaming", fase: "leyendo", desde: Date.now() }]);
    setStreaming(true);
    const ctrl = new AbortController(); abortRef.current = ctrl;
    let convId = conversationId;
    let errorMsg: string | null = null;
    const patch = (fn: (m: ChatMsg) => ChatMsg) => setMessages((xs) => xs.map((m) => (m.id === asstId ? fn(m) : m)));
    const esperar = (ms: number) => new Promise<void>((r) => { const t = setTimeout(r, ms); ctrl.signal.addEventListener("abort", () => { clearTimeout(t); r(); }, { once: true }); });
    try {
      for (let intento = 0; ; intento++) {
        errorMsg = null;
        let code = "";
        let huboTexto = false;
        let transitorio = false;
        try {
          const res = await fetch("/api/chat", { method: "POST", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }, body: JSON.stringify({ conversationId: convId ?? undefined, message: text }), signal: ctrl.signal });
          if (!res.ok || !res.body) {
            let msg = "El asesor no pudo responder.";
            try { const j = (await res.json()) as { error?: { code?: string; message?: string } }; msg = j.error?.message ?? msg; code = j.error?.code ?? ""; } catch { /* sin json */ }
            errorMsg = msg;
            transitorio = res.status >= 500; // 4xx son límites o datos: no se reintentan
          } else {
            const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = "";
            for (;;) {
              const { value, done } = await reader.read();
              if (done) break;
              buf += dec.decode(value, { stream: true });
              let idx: number;
              while ((idx = buf.indexOf("\n\n")) >= 0) {
                const chunk = buf.slice(0, idx); buf = buf.slice(idx + 2);
                const line = chunk.split("\n").find((l) => l.startsWith("data: "));
                if (!line) continue;
                const ev = JSON.parse(line.slice(6)) as Ev;
                if (ev.type === "meta") { convId = ev.conversationId; setConversationId(ev.conversationId); }
                else if (ev.type === "status") { if (ev.phase === "pensando") patch((m) => (m.text ? m : { ...m, fase: "pensando" })); }
                else if (ev.type === "text") { huboTexto = true; patch((m) => ({ ...m, text: m.text + ev.text, fase: "redactando" })); }
                else if (ev.type === "tool" && ev.status === "start") patch((m) => ({ ...m, tools: [...m.tools, ev.name], fase: m.text ? m.fase : "herramientas" }));
                else if (ev.type === "refusal") patch((m) => ({ ...m, status: "refusal", text: m.text || "No puedo ayudarte con eso aquí. Si es sobre tu diseño patrimonial, reformúlalo." }));
                else if (ev.type === "error") { errorMsg = ev.message; code = ev.code ?? ""; transitorio = ev.code === "SATURADO"; }
                else if (ev.type === "done") patch((m) => (m.status === "streaming" ? { ...m, status: "ok" } : m));
              }
            }
            if (!errorMsg) patch((m) => (m.status === "streaming" ? { ...m, status: "ok" } : m));
          }
        } catch (e) {
          if ((e as Error).name === "AbortError") { patch((m) => (m.status === "streaming" ? { ...m, status: "ok" } : m)); break; } // el miembro lo detuvo: se queda lo que llegó
          errorMsg = "Se cortó la conexión. Intenta de nuevo.";
          transitorio = !huboTexto;
        }
        if (!errorMsg) break;
        const reintentar = transitorio && !huboTexto && intento < REINTENTOS && !ctrl.signal.aborted;
        if (!reintentar) { const msg = errorMsg, c = code; patch((m) => ({ ...m, status: "error", text: m.text || msg, code: c })); break; }
        patch((m) => ({ ...m, text: "", tools: [], fase: "reintentando", intento: intento + 1 }));
        await esperar(ESPERA_REINTENTO_MS[intento] ?? 7000);
        if (ctrl.signal.aborted) { patch((m) => ({ ...m, status: "error", text: errorMsg!, code })); break; }
        patch((m) => ({ ...m, fase: "leyendo", desde: Date.now() }));
      }
    } finally {
      setStreaming(false); abortRef.current = null;
      // Si salió bien, sincroniza ids reales (para reportar). Si falló, se queda la burbuja de error con "Reintentar"; recargar la borraría.
      if (convId && !errorMsg) void load(convId);
    }
    return errorMsg;
  }, [conversationId, load]);

  /** Vuelve a mandar el último mensaje tras un error: quita la burbuja fallida y su pregunta y las crea de nuevo. */
  const retry = useCallback(async (): Promise<string | null> => {
    const t = ultimoTextoRef.current;
    if (!t) return null;
    setMessages((xs) => { const i = xs.length - 1; return xs[i]?.role === "assistant" && xs[i].status === "error" && xs[i - 1]?.role === "user" ? xs.slice(0, i - 1) : xs; });
    return send(t);
  }, [send]);

  const stop = useCallback(() => abortRef.current?.abort(), []);
  const nueva = useCallback(() => { setConversationId(null); setMessages([]); }, []);

  return { messages, conversationId, streaming, send, retry, stop, load, nueva };
}
